import { useState } from "react";
import { Link } from "react-router-dom";
import { mockResellerUser, mockSalesActivity } from "@/mocks/reseller";
import ResellerSidebar from "@/pages/reseller/components/ResellerSidebar";
import StatsCards from "@/pages/reseller/components/StatsCards";
import CommissionTable from "@/pages/reseller/components/CommissionTable";
import ReferralLinksPanel from "@/pages/reseller/components/ReferralLinksPanel";
import PayoutPanel from "@/pages/reseller/components/PayoutPanel";

const platformIcons: Record<string, string> = {
  TikTok: "ri-tiktok-line",
  YouTube: "ri-youtube-line",
  Instagram: "ri-instagram-line",
  Douyin: "ri-tiktok-line",
  WeChat: "ri-wechat-line",
};

const ResellerDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const maxSales = Math.max(...mockSalesActivity.map((d) => d.sales));

  return (
    <div className="min-h-screen bg-[#f8f7f5] flex" style={{ fontFamily: "'Inter', sans-serif" }}>
      <ResellerSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden w-8 h-8 flex items-center justify-center text-gray-600 cursor-pointer"
              onClick={() => setSidebarOpen(true)}
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Reseller Dashboard</h1>
              <p className="text-xs text-gray-400">
                Welcome back, {mockResellerUser.name} &bull;{" "}
                <span className="inline-flex items-center gap-1">
                  <i className={`${platformIcons[mockResellerUser.platform] || "ri-share-line"} text-xs`}></i>
                  {mockResellerUser.platform}
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 bg-[#D4AF37]/10 px-3 py-2 rounded-lg">
              <i className="ri-vip-crown-line text-[#D4AF37] text-sm"></i>
              <span className="text-xs font-bold text-[#B8860B]">{mockResellerUser.tier}</span>
            </div>
            <Link
              to="/products"
              className="flex items-center gap-2 bg-[#C41E3A] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#a01830] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              <span className="hidden sm:inline">Browse Products</span>
            </Link>
          </div>
        </header>

        <main className="flex-1 p-6 space-y-6">
          {/* Welcome Banner */}
          <div className="bg-gradient-to-r from-[#1a0a0a] via-[#3a1010] to-[#C41E3A] rounded-xl p-6 text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img
                src={mockResellerUser.avatar}
                alt={mockResellerUser.name}
                className="w-14 h-14 rounded-full object-cover object-top border-2 border-[#D4AF37]"
              />
              <div>
                <div className="text-white/70 text-xs mb-1">Your Referral Code</div>
                <div className="text-2xl font-extrabold tracking-widest text-[#D4AF37]">{mockResellerUser.referralCode}</div>
                <div className="text-white/60 text-xs mt-1">Share this code to earn commission on every sale</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {["TikTok", "Douyin", "Instagram", "YouTube", "WeChat"].map((p) => (
                <div key={p} className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-full">
                  <i className={`${platformIcons[p] || "ri-share-line"} text-xs text-white`}></i>
                  <span className="text-xs text-white/80">{p}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <StatsCards />

          {/* Sales Chart + PayPal */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Mini Bar Chart */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-bold text-gray-900 text-sm">Sales Activity (Last 7 Days)</h2>
                <span className="text-xs text-gray-400">April 2024</span>
              </div>
              <div className="flex items-end gap-3 h-32">
                {mockSalesActivity.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                    <div className="text-[10px] font-bold text-[#C41E3A]">{d.sales}</div>
                    <div
                      className="w-full bg-gradient-to-t from-[#C41E3A] to-[#D4AF37] rounded-t-md transition-all"
                      style={{ height: `${(d.sales / maxSales) * 96}px` }}
                    ></div>
                    <div className="text-[9px] text-gray-400 whitespace-nowrap">{d.date}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-sm font-bold text-gray-900">214</div>
                  <div className="text-[10px] text-gray-400">Total Sales</div>
                </div>
                <div className="border-x border-gray-100">
                  <div className="text-sm font-bold text-[#C41E3A]">$4,200</div>
                  <div className="text-[10px] text-gray-400">Commission</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900">6.8%</div>
                  <div className="text-[10px] text-gray-400">Conversion</div>
                </div>
              </div>
            </div>

            {/* PayPal Payout */}
            <PayoutPanel />
          </div>

          {/* Commission Table */}
          <CommissionTable />

          {/* Referral Links */}
          <ReferralLinksPanel />

          {/* How It Works */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="font-bold text-gray-900 text-sm mb-5">How Reseller Commissions Work</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { step: "01", icon: "ri-store-2-line", title: "Browse Factory Products", desc: "Choose from USA & Canada factory and farm products. Each factory sets their own commission rate." },
                { step: "02", icon: "ri-links-line", title: "Get Your Referral Link", desc: "Generate a unique referral link for any product. Share it on TikTok, Douyin, Instagram, YouTube, or WeChat." },
                { step: "03", icon: "ri-shopping-cart-line", title: "Customer Buys", desc: "When someone clicks your link and buys, the sale is tracked automatically. You see it in real-time." },
                { step: "04", icon: "ri-paypal-line", title: "Receive PayPal Payout", desc: "Commission is paid to your PayPal account every month. Request early payout anytime above $100." },
              ].map((item) => (
                <div key={item.step} className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#C41E3A] rounded-full text-white text-xs font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center bg-[#D4AF37]/10 rounded-lg">
                      <i className={`${item.icon} text-[#B8860B] text-base`}></i>
                    </div>
                  </div>
                  <div className="font-semibold text-gray-900 text-sm">{item.title}</div>
                  <div className="text-xs text-gray-500 leading-relaxed">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ResellerDashboard;
